import React, {Component} from 'react';
import PropTypes from "prop-types";
import {findKey, forEach, get, isEmpty, isEqual, isUndefined, last, map, set} from "lodash";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import cellEditFactory from 'react-bootstrap-table2-editor';
import RemoveModal from "./RemoveModal";
import SearchInput from "./SearchInput";
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from "reactstrap";
import classnames from "classnames";
import windowDimensions from "../../utils/windowDimensions";
import Button from "../../components/Button";
import history from "utils/history";
import TableSettings, {getLocalStorageColumnsKey} from "./components/TableSettings";
import {Translation} from "react-i18next";

class BTable extends Component {
  constructor(props) {
    super(props);

    this.scroll = 0;

    this.state = {
      sizePerPageDropdown: false,
    };
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    const t = document.getElementById(this.props.id);
    this.scroll = t.parentElement.scrollTop;

    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const t = document.getElementById(this.props.id);
    if (t) {
      t.parentElement.scrollTop = this.scroll;
    }
  }

  sizePerPageRenderer = ({ options, currSizePerPage, onSizePerPageChange }) => {
    const {sizePerPageDropdown} = this.state;

    return <Dropdown tag="span" isOpen={sizePerPageDropdown} direction={'up'} toggle={() => this.setState({sizePerPageDropdown: !sizePerPageDropdown})}>
      <DropdownToggle color={'light'} caret>{currSizePerPage}</DropdownToggle>
      <DropdownMenu>
        {options.map((option, key) => {
          const active = currSizePerPage === `${option.page}`;
          return <DropdownItem
            key={key}
            active={active}
            onClick={ () => onSizePerPageChange(option.page) }
          >{option.text}</DropdownItem>

        })}
      </DropdownMenu>
    </Dropdown>
  };

  paginationTotalRenderer = (from, to, size) => {
    return <React.Fragment>
      <span className="react-bootstrap-table-pagination-total mx-2">
        <span className="d-none d-md-inline"><Translation>{t => t('app_table:pagination_total_long', {from, to, size})}</Translation></span>
        <span className="d-inline d-md-none"><Translation>{t => t('app_table:pagination_total_short', {from, to, size})}</Translation></span>
      </span>
      {/*<span className="d-inline-block"><Button color={'light'} tooltip={'Drukuj tabelę'} onClick={() => window.print()}><i className="fas fa-print" /></Button></span>*/}

      {(this.props.tableKeyName && this.props.tableSettingsEnable) && <TableSettings
        tableKeyName={this.props.tableKeyName}
        columns={this.props.columns}
        onChanged={this.props.onTableSettingsChanged}
      />}
    </React.Fragment>
  }

  getColumns = () => {
    let columns = this.props.columns.map(column => {
      if(column.choosable){
        column.formatter = (cell, row) => {
          return <div className="d-flex justify-content-between align-items-center">
            {column.formatterChoosable(cell, row)}
            <div className="flex-grow-1">
              <Button
                size={'sm'}
                className="row-select-btn hover-display text-center ml-auto"
                onClick={(e) => {
                  e.stopPropagation()
                  this.props.onChoose(row)
                }}
              ><Translation>{t => t('app_table:choosable_choose')}</Translation></Button>
            </div>
          </div>
        }
      }

      return column
    })

    if(this.props.tableKeyName && this.props.tableSettingsEnable){
      const columnLastVisible = last(columns.filter(column => !column.hidden && !column.smallViewColumn));
      if(columnLastVisible){
        columns = columns.map(column => {
          if(column.dataField === columnLastVisible.dataField && column?.headerStyle?.width){
            column.headerStyle.widthDefault = column.headerStyle.width
            column.headerStyle.width = null
          }

          return column;
        })
      }
    }

    return columns;
  }

  render() {
    const {
      id,
      data,
      tableWrapper,
      tableRef,
      page,
      sizePerPage,
      onTableChange,
      totalSize,
      loading,
      selectRow,
      editable,
      remote,
      sortField,
      sortOrder,
      pagination,
      rowClasses,
      noScroll,
      disableMinHeightTable,
      tableSize,
      maxHeight,
    } = this.props;

    return (
      <div className={classnames({
        'table-wrapper': tableWrapper,
        'd-flex flex-column flex-grow-1': true,
        'no-scroll': noScroll,
      })} style={{
        minHeight: disableMinHeightTable ? 'auto' : undefined,
        height: disableMinHeightTable ? 'auto' : undefined,
        maxHeight: maxHeight ?? undefined,
      }}>
        <BootstrapTable
          id={id}
          ref={tableRef}
          bootstrap4
          classes={`table-${tableSize}`}
          bordered={false}
          wrapperClasses={classnames({
            'table-scroll-x': true,
            'loading-data': loading,
          })}
          rowClasses={rowClasses}
          hover
          remote={remote}
          keyField="__uKey"
          data={ data }
          columns={this.getColumns()}
          pagination={ pagination ? paginationFactory({
            page,
            sizePerPage: parseInt(sizePerPage),
            totalSize,
            sizePerPageList: [10, 15, 25, 50, 100, 250].map(pageValue => ({
              text: pageValue, value: pageValue,
            })),
            sizePerPageRenderer: this.sizePerPageRenderer,
            showTotal: true,
            paginationTotalRenderer: this.paginationTotalRenderer,
          }) : undefined }
          cellEdit={ editable ? cellEditFactory({
            mode: 'click',
          }) : undefined }
          selectRow={selectRow}
          onTableChange={ onTableChange }
          loading={loading}
          noDataIndication={() => !loading ? <Translation>{t => t('common:no_data')}</Translation> : <span style={{visibility: 'hidden'}}>.</span>}
          sort={{
            dataField: sortField,
            order: sortOrder,
            sortCaret: (order, column) => {
              if (!order) return '';
              else if (order === 'asc') return <i className="fas fa-caret-up ml-1" /> ;
              else if (order === 'desc') return <i className="fas fa-caret-down ml-1" />;
              return null;
            }
          }}
        />
      </div>
    );
  }
}

class AppTable extends Component {
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();

    const params = new URLSearchParams(window.location.search);
    const qFromUrl = this.props.modifyUriParams ? params.get('q') : undefined;
    const pageFromUrl = this.props.modifyUriParams ? params.get('page') || 1 : 1
    const itemsPerPageFromUrl = this.props.modifyUriParams ? params.get('itemsPerPage') || this.getDefaultPropsValueUsingLocalStorage('sizePerPage') : this.getDefaultPropsValueUsingLocalStorage('sizePerPage')

    this.state = {
      reRenderingTable: false,
      fetchDataTimeout: false,
      tableId: props.id ?? btoa(Math.random()).substr(0, 10),
      searchQuery: qFromUrl ?? '',
      data: this.props.data ?? [],
      page: parseInt(pageFromUrl),
      sizePerPage: itemsPerPageFromUrl,
      totalSize: 0,
      sortField: this.getDefaultPropsValueUsingLocalStorage('sortField'),
      sortOrder: this.getDefaultPropsValueUsingLocalStorage('sortOrder'),
      loading: false,
      selected: [],
      selectedCount: 0,
      lastThreeSortKeys: [],
      isInitialLoad: true,
    };
  }
  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.isInitialLoad && !this.state.loading && prevState.loading) {
      this.setState({ isInitialLoad: false });
    }

    if(
      !isUndefined(this.props.data)
      && (
        (prevProps.data !== this.props.data)
        || (this.state.data !== this.props.data)
      )
    ){
      this.setState({
        data: this.props.data,
        totalSize: this.props.data.length,
      });
    }

    if(
      !isEqual(prevState.page, this.state.page)
      || !isEqual(prevState.sizePerPage, this.state.sizePerPage)
      || !isEqual(prevState.searchQuery, this.state.searchQuery)
      || !isEqual(prevState.sortField, this.state.sortField)
      || !isEqual(prevState.sortOrder, this.state.sortOrder)
      || !isEqual(prevProps.filters, this.props.filters)
    ) {
      this.fetchData();
      if (this.props.modifyUriParams) {
        this.updateUrlAndState({});
      }
    }

    if(!isEqual(prevState.sizePerPage, this.state.sizePerPage)){
      if(this.getTableLocalStorageKey('sizePerPage')) {
        localStorage[this.getTableLocalStorageKey('sizePerPage')] = this.state.sizePerPage
      }
    }

    if(!isEqual(prevState.sortField, this.state.sortField)){
      if(this.getTableLocalStorageKey('sortField')) {
        localStorage[this.getTableLocalStorageKey('sortField')] = this.state.sortField
      }
    }

    if(!isEqual(prevState.sortOrder, this.state.sortOrder)){
      if(this.getTableLocalStorageKey('sortOrder')) {
        localStorage[this.getTableLocalStorageKey('sortOrder')] = this.state.sortOrder
      }
    }
  }

  reRenderTable = () => {
    this.setState({
      reRenderingTable: true,
    }, () => {
      setTimeout(() => {
        this.setState({
          reRenderingTable: false,
        })
      }, 1)
    })
  }

  getTableLocalStorageKey = (property) => {
    if(!this.props.tableKeyName){
      return false
    }

    switch(property) {
      case 'sizePerPage':
        return 't' + this.props.tableKeyName
      case 'sortField':
        return `t${this.props.tableKeyName}sortField`
      case 'sortOrder':
        return `t${this.props.tableKeyName}sortOrder`
    }

    return false;
  }

  getDefaultPropsValueUsingLocalStorage = (property) => {
    const key = this.getTableLocalStorageKey(property);
    return (key && key in localStorage) ? localStorage[key] : this.props[property]
  }

  fetchData = () => {
    document.getElementById(this.state.tableId).parentElement.scrollTop = 0;

    if(!this.props.apiFnList){
      return;
    }

    clearTimeout(this.state.fetchDataTimeout);
    this.setState({
      fetchDataTimeout: setTimeout(() => {
        this.setState({
          loading: true,
        }, () => {
          this.selectedChange([]);
        });
        let queryParams = {
          q: this.state.searchQuery,
          page: this.state.page,
          itemsPerPage: this.state.sizePerPage,
          ...this.props.filters,
        };

        if(this.props.sortFieldConstant){
          queryParams[`order[${this.props.sortFieldConstant}]`] = this.props.sortOrderConstant;
        }

        if(this.state.sortField){
          queryParams[`order[${this.state.sortField}]`] = this.state.sortOrder;
        }

        if(!isEmpty(this.props.sortConstantEnd)){
          forEach(this.props.sortConstantEnd, (sort, order) => {
            const sortFinal = sort ?? this.state.sortOrder
            if(sortFinal){
              queryParams[`order[${order}]`] = sortFinal;
            }
          })
        }

        this.props.apiFnList(map(queryParams, (value, key) => [key, value].join('=')).join('&'))
          .then(res => {
            this.setState({
              data: res.data['hydra:member'],
              totalSize: res.data['hydra:totalItems'],
              loading: false,
            });

            if(this.props.onDataFetched){
              this.props.onDataFetched(res.data['hydra:member'])
            }
          })
          .catch(res => {
            this.setState({
              loading: false,
            });
          });
      }, 10)
    })
  }

  getTableData = () => {
    let _data = this.props.getData(this.state.data).map((d, k) => {
      let __uKey = d?.id;
      if(!__uKey){
        __uKey = 'i' + k?.toString() + (d?.id ? d.id.toString() : '') + (!isUndefined(d?.position) ? d?.position?.toString() : '');
      }

      d.__uKey = __uKey;

      return d
    })

    if(!this.props.apiFnList && this.state.searchQuery && this.props.searchInProperty.length > 0){
      const search = this.state.searchQuery.split(' ');
      _data = _data.filter(row => this.props.searchInProperty.filter(propKey => {
        let exists = false;
        const propKeyIsArray = propKey.includes('[].')
        const propKeySplit = propKey.split('[].')

        try {
          search.forEach(s => {
            s = s.replace(/[^A-Za-z0-9]/g, '').toLowerCase()

            let matches = false
            if(propKeyIsArray){
              get(row, propKeySplit[0]).forEach((prop) => {
                if(get(prop, propKeySplit[1])?.toLowerCase()?.replace(/[^A-Za-z0-9]/g, '')?.indexOf(s) > -1){
                  matches = true
                  exists = true;
                  throw 'Break';
                }
              })
            }else{
              matches = get(row, propKey)?.toLowerCase()?.replace(/[^A-Za-z0-9]/g, '')?.indexOf(s) > -1
            }

            if(matches){
              exists = true;
              throw 'Break';
            }
          })
        }catch(e) { }

        return exists;
      }).length > 0)
    }

    return _data
  }

  unselectAll = () => {
    setTimeout(() => {
      this.setState({
        selected: [],
        selectedCount: 0,
      });

      if (this.props.onSelectedChange) {
        this.props.onSelectedChange([]);
      }
    }, 1)
  }

  selectedChange = (selectedNew) => {
    this.setState({
      selected: selectedNew,
      selectedCount: selectedNew.length,
    });

    if (this.props.onSelectedChange) {
      this.props.onSelectedChange(selectedNew);
    }
  }

  handleOnSelect = (row) => {
    let selectedNew = this.state.selected;

    if(this.state.selected.find(sel => sel.__uKey === row.__uKey)){
      selectedNew = this.state.selected.filter(sel => sel.__uKey !== row.__uKey)
    }else{
      selectedNew.push(row);
    }

    this.selectedChange(selectedNew);
  }

  handleOnSelectAll = (isSelect, rows, e) => {
    let selectedNew = [];

    if(isSelect){
      selectedNew = rows;
    }

    this.selectedChange(selectedNew);
  }

  handleTableChange = (type, values) => {
    switch (type) {
      case 'cellEdit': {
        const {cellEdit} = values;
        const column = this.props.columns.find(c => c.dataField === cellEdit.dataField)
        let propertyPath = column?.propertyPath ?? cellEdit.dataField

        let update = {
          [propertyPath]: cellEdit.newValue,
        }
        if(propertyPath.indexOf('.') !== false){
          update = set({}, propertyPath, cellEdit.newValue);
        }

        this.props.apiFnUpdate({
          id: cellEdit.rowId,
          ...update,
        })
        .then((() => {
          this.fetchData();
        }))
        .catch((() => {
          this.fetchData();
        }));

        break;
      }
      case 'pagination': {
        const { page, sizePerPage } = values;
        this.updateUrlAndState({ page, sizePerPage });
        this.selectedChange([]);

        break;
      }
      case 'sort': {
        if (this.state.isInitialLoad) {
          return;
        }

        let lastThreeSortKeys = this.state.lastThreeSortKeys;

        lastThreeSortKeys.push(values.sortField);
        this.selectedChange([]);
        let stateChange = {
          page: 1,
        }

        if(lastThreeSortKeys.length >= 3 && lastThreeSortKeys.every( (val, i, arr) => val === arr[0] )){
          stateChange.sortField = this.props.sortField;
          stateChange.sortOrder = this.props.sortOrder;
          stateChange.lastThreeSortKeys = [];
        }else{
          stateChange.sortField = values.sortField;
          stateChange.sortOrder = values.sortOrder;
          stateChange.lastThreeSortKeys = lastThreeSortKeys.slice(-2);
        }

        this.updateUrlAndState(stateChange);

        break;
      }
    }
  }

  getColumns = () => {
    let columnsSettings = JSON.parse(localStorage.getItem(getLocalStorageColumnsKey(this.props.tableKeyName)))
    if(columnsSettings && columnsSettings.filter(column => column.isActive === true).length === 0){
      columnsSettings = null
      localStorage.removeItem(getLocalStorageColumnsKey(this.props.tableKeyName))
    }
    const columnsSettingsOrder = columnsSettings?.map(c => c.id)

    let columns = this.props.columns
      // .sort((a,b) => {
      //   if(!columnsSettingsOrder){
      //     return 1
      //   }
      //
      //   return columnsSettingsOrder.indexOf(a.dataField) > columnsSettingsOrder.indexOf(b.dataField) ? 1 : -1
      // })
      .filter(column => column.disabled !== true)
      .filter(column => {
        if(!isUndefined(this.props.smallViewColumn)){
          if(windowDimensions().width >= this.props.smallViewColumn){
            return !column.smallViewColumn
          }else{
            return !!column.smallViewColumn
          }
        }

        return true;
      })
      .map(column => {
        if(!isUndefined(this.props.smallViewColumn)){
          if(column.smallViewColumn){
            column.hidden = windowDimensions().width >= this.props.smallViewColumn;
          }else{
            column.hidden = column.hidden ? true : windowDimensions().width < this.props.smallViewColumn;
          }
        }

        column.headerSortingClasses = 'column-sorted'

        return column;
      })

    if(this.props.tableSettingsEnable){
      columns = columns
        .map((column, position) => {
          column.position = position;
          if(!column.smallViewColumn){
            let columnSettings;
            let columnSettingKey;
            if(columnsSettings){
              columnSettingKey = findKey(columnsSettings, cs => cs.id === column.dataField);
              columnSettings = columnsSettings.find(c => c.id === column.dataField)
            }

            if(columnSettings){
              column.hidden = !columnSettings.isActive
              column.headerStyle.width = columnSettings.width
              column.position = columnSettingKey;
            }else{
              column.hidden = !column.defaultDisplayable
            }
          }

          return column;
        })
        .sort((a, b) => parseInt(a.position) > parseInt(b.position) ? 1 : -1)
    }

    return columns;
  }

  updateUrlAndState = (newStateData) => {
    const { modifyUriParams } = this.props;
    const params = new URLSearchParams(window.location.search);

    const updatedState = { ...this.state, ...newStateData };
    params.set('page', updatedState.page);
    params.set('itemsPerPage', updatedState.sizePerPage);
    if (updatedState.searchQuery) {
      params.set('q', updatedState.searchQuery);
    } else {
      params.delete('q');
    }

    Object.entries(this.props.filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    if (modifyUriParams) {
      history.replace({
        pathname: window.location.pathname,
        search: params.toString(),
      });
    }

    if (!isEmpty(newStateData)) {
      this.setState(newStateData);
    }
  };

  render() {
    return (
      <div className={classnames({
        'h-100': isEmpty(this.props.maxHeight),
        'd-flex': true,
      })}>
        <div className="app-table d-flex flex-column flex-grow-1 w-100">
          <div className="d-flex d-print-none justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {this.props.addButton && <div className="mr-2">{this.props.addButton}</div>}
              {this.props.onRemoved && <RemoveModal
                size={this.props.tableSize}
                deleteFunc={this.props.apiFnDelete ? this.props.apiFnDelete : undefined}
                propertyDeleteIdPath={this.props.propertyDeleteIdPath}
                selected={this.state.selected}
                onDeleted={() => {
                  this.props.onRemoved(this.state.selected);
                  this.selectedChange([]);
                  this.fetchData();
                }}
              />}
              {this.props.additionalButtons && <div>
                {this.props.additionalButtons.map((Component, key) => <div key={key} className="ml-2">
                  {React.createElement(Component, {
                    appTable: this,
                    checked: this.state.selected
                  })}
                </div>)}
              </div>}
            </div>

            <div className="d-flex flex-wrap align-items-center justify-content-end">
              {this.props.filtersComponents.map((FilterComponent, index) => (
                <div key={index} className="ml-2">
                  <FilterComponent />
                </div>
              ))}
              {this.props.searchable && <div className="ml-2">
                <SearchInput
                  codeCameraScanner={this.props.searchCodeCameraScanner}
                  inputVisible={this.props.searchFocused}
                  onInputChanged={text => {
                    const currentSearchQuery = this.state.searchQuery || '';

                    if (currentSearchQuery === text) {
                      return;
                    }

                    this.updateUrlAndState({
                      searchQuery: text,
                      page: 1,
                    });

                    if (this.props.onSearchQueryChange) {
                      this.props.onSearchQueryChange(text);
                    }
                  }}
                />
              </div>}
            </div>
          </div>
          {!this.state.reRenderingTable && <BTable
            tableRef={this.tableRef}
            id={this.state.tableId}
            tableKeyName={this.props.tableKeyName}
            tableSize={this.props.tableSize}
            maxHeight={this.props.maxHeight}
            tableSettingsEnable={this.props.tableSettingsEnable}
            data={this.getTableData()}
            disableMinHeightTable={this.props.disableMinHeightTable}
            tableWrapper={this.props.tableWrapper}
            pagination={this.props.pagination}
            columns={this.getColumns()}
            page={this.state.page}
            sizePerPage={this.state.sizePerPage}
            totalSize={this.state.totalSize}
            onTableChange={this.handleTableChange}
            loading={this.state.loading}
            editable={this.props.editable}
            remote={!!this.props.apiFnList}
            selectRow={(this.props.apiFnDelete || this.props.onSelectedChange) ? {
              clickToSelect: this.props.clickToSelect,
              mode: 'checkbox',
              bgColor: '#c7edff',
              selected: this.state.selected.map(c => c.__uKey),
              selectionHeaderRenderer: () => {
                return <i className="fas fa-retweet"/>;
              },
              onSelect: this.handleOnSelect,
              onSelectAll: this.handleOnSelectAll,
            } : undefined}
            sortField={this.state.sortField}
            sortOrder={this.state.sortOrder}
            rowClasses={this.props.rowClasses}
            noScroll={this.props.noScroll}
            onChoose={this.props.onChoose}
            onTableSettingsChanged={() => this.reRenderTable()}
          />}
        </div>
      </div>
    );
  }
}

AppTable.defaultProps = {
  tableSize: 'md',
  tableWrapper: true,
  searchCodeCameraScanner: false,
  tableSettingsEnable: false,
  clickToSelect: false,
  pagination: true,
  propertyDeleteIdPath: 'id',
  sizePerPage: 25,
  searchable: true,
  searchFocused: false,
  editable: false,
  addButton: undefined,
  additionalButtons: [],
  sortFieldConstant: null,
  sortOrderConstant: null,
  sortConstantEnd: {},
  sortField: 'id',
  sortOrder: 'desc',
  getData: (data) => {
    return data;
  },
  noScroll: false,
  searchInProperty: [],
  disableMinHeightTable: false,
  modifyUriParams: true,
  filtersComponents: [],
};

AppTable.propTypes = {
  tableSize: PropTypes.string,
  maxHeight: PropTypes.string,
  onSearchQueryChange: PropTypes.func,
  tableWrapper: PropTypes.bool,
  searchCodeCameraScanner: PropTypes.bool,
  clickToSelect: PropTypes.bool,
  tableKeyName: PropTypes.string,
  tableSettingsEnable: PropTypes.bool,
  getData: PropTypes.func,
  pagination: PropTypes.bool,
  propertyDeleteIdPath: PropTypes.string,
  filters: PropTypes.object,
  sortFieldConstant: PropTypes.string,
  sortOrderConstant: PropTypes.string,
  sortConstantEnd: PropTypes.object,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  addButton: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  additionalButtons: PropTypes.array,
  onRemoved: PropTypes.func,
  searchable: PropTypes.bool,
  searchFocused: PropTypes.bool,
  editable: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({
    dataField: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,).isRequired,
  data: PropTypes.array,
  apiFnList: PropTypes.func,
  apiFnDelete: PropTypes.func,
  apiFnUpdate: PropTypes.func,
  sizePerPage: PropTypes.number,
  onSelectedChange: PropTypes.func,
  rowClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.func,]),
  noScroll: PropTypes.bool,
  searchInProperty: PropTypes.arrayOf(PropTypes.string),
  disableMinHeightTable: PropTypes.bool,
  onDataFetched: PropTypes.func,
  modifyUriParams: PropTypes.bool,
  filtersComponents: PropTypes.arrayOf(PropTypes.elementType),
};

export default AppTable;
